import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { UpdateSnackbar } from "@/components/UpdateSnackbar";
import { RouteTracker } from "@/components/analytics/RouteTracker";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ChildProfilesProvider } from "./contexts/ChildProfilesContext";
import { TourProvider } from "./components/Tour/TourProvider";
import { NavigationProgress } from "./components/common/NavigationProgress";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { ScrollToTop } from "./components/ScrollToTop";
import { FeedbackInviteModal } from '@/components/Feedback/FeedbackInviteModal';
import { FeedbackSurvey } from '@/components/Feedback/FeedbackSurvey';
import { useFeedbackSurvey } from '@/hooks/useFeedbackSurvey';

// PERFORMANCE OPTIMIZATION: Eager load critical pages (including onboarding flow)
// Onboarding pages MUST be eager-loaded to prevent MIME type errors from stale chunks
// Scripts is eager-loaded to prevent chunk failures on iOS PWA
import Auth from "./pages/Auth";
import Welcome from "./pages/Welcome";
import WelcomeHotmart from "./pages/WelcomeHotmart";
import Blocked from "./pages/Blocked";
import Dashboard from "./pages/Dashboard";
import OnboardingWelcome from "./pages/OnboardingWelcome";

import Scripts from "./pages/Scripts";
// A frase aberta vira página (Phrases refeita, 25/09/2026); eager pelo mesmo motivo.
import PhraseDetail from "./pages/PhraseDetail";
// Versão francesa (09/10/2026): a entrada por código é eager como as outras de entrada.
import AuthCodeFr from "./pages/AuthCodeFr";
import { IS_FR, tr } from "@/lib/i18n";

// PERFORMANCE: Lazy load onboarding-only and rarely-visited pages
const NotFound = lazy(() => import("./pages/NotFound"));
const PWAInstall = lazy(() => import("./pages/PWAInstall"));
const PWACheck = lazy(() => import("./pages/PWACheck"));
const ThemeSelection = lazy(() => import("./pages/ThemeSelection"));


// PERFORMANCE OPTIMIZATION: Lazy load non-critical pages for code splitting
// This reduces initial bundle size and improves Time to Interactive (TTI)
const RefundStatus = lazy(() => import("./pages/RefundStatus"));
const LegalPendingFr = lazy(() => import("./pages/LegalPendingFr"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Contact = lazy(() => import("./pages/Contact"));
const Quiz = lazy(() => import("./pages/Quiz"));
const Profile = lazy(() => import("./pages/Profile"));
const ProfileEdit = lazy(() => import("./pages/Profile/Edit"));
const Purchases = lazy(() => import("./pages/Purchases"));
const Family = lazy(() => import("./pages/Family"));
const FamilyWelcome = lazy(() => import("./pages/FamilyWelcome"));
const Invite = lazy(() => import("./pages/Invite"));

const Learn = lazy(() => import("./pages/Learn"));
const EbookReader = lazy(() => import("./pages/EbookReaderV2"));
const Admin = lazy(() => import("./pages/Admin"));
const MagicWords = lazy(() => import("./pages/MagicWords"));
const HomeFr = lazy(() => import("./pages/HomeFr"));
const GenerateWelcomePDF = lazy(() => import("./pages/GenerateWelcomePDF"));
// Achievements page removed — badges shown in Profile
const UserProfile = lazy(() => import("./pages/UserProfile"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const JoinFamily = lazy(() => import("./pages/JoinFamily"));
const Gift = lazy(() => import("./pages/Gift"));
const Guides = lazy(() => import("./pages/Programs"));
const GuideDetail = lazy(() => import("./pages/ProgramDetail"));
const GuideLesson = lazy(() => import("./pages/ProgramLesson"));
// 21/09/2026: CalmMomSystemPage e PremiumContent viraram "Your purchases".
const YourPurchases = lazy(() => import("./pages/YourPurchases"));
const ExpertChat = lazy(() => import("./pages/ExpertChat"));
const NutritionGuide = lazy(() => import("./pages/NutritionGuide"));
const SleepChallenge = lazy(() => import("./pages/SleepChallenge"));
const BabyPhase = lazy(() => import("./pages/BabyPhase"));
const ToddlerPhase = lazy(() => import("./pages/ToddlerPhase"));
const SleepChallengeLesson = lazy(() => import("./pages/SleepChallengeLesson"));
const ADHDGuide = lazy(() => import("./pages/ADHDGuide"));
const EbookReaderV3 = lazy(() => import("./pages/EbookReaderV3"));


// Loading fallback component for Suspense
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-muted-foreground">{tr("Loading...")}</p>
    </div>
  </div>
);

// PERFORMANCE: Optimized React Query configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000, // 10 minutes - increased for better caching
      gcTime: 60 * 60 * 1000, // 60 minutes - keep data longer
      refetchOnWindowFocus: false, // Only refetch when explicitly needed
      refetchOnReconnect: true, // Refetch on network reconnect
      retry: 1, // Only retry once on failure
      networkMode: 'offlineFirst', // Better offline support
    },
    mutations: {
      retry: 1, // Retry failed mutations once
      networkMode: 'offlineFirst', // Better offline support
    },
  },
});

// ============================================================================
// A publicação francesa (09/10/2026, VITE_APP_LOCALE=fr): só o núcleo. Entrada
// por código, quiz, Sarah, as 3 frases e o perfil. Livros, frases, guias e o
// resto do conteúdo ainda não existem em francês, então os endereços deles
// levam para a conversa em vez de abrir uma tela em inglês.
// ============================================================================
function FrRoutes() {
  const p = (el: JSX.Element) => <ProtectedRoute>{el}</ProtectedRoute>;
  return (
    <Routes>
      <Route path="/auth" element={<AuthCodeFr />} />
      <Route path="/welcome-hp" element={<AuthCodeFr />} />
      <Route path="/welcome" element={<AuthCodeFr />} />
      <Route path="/reset-password" element={<Navigate to="/auth" replace />} />
      <Route path="/blocked" element={<Blocked />} />
      <Route path="/pwa-install" element={p(<PWAInstall />)} />
      <Route path="/pwa-check" element={p(<PWACheck />)} />
      <Route path="/theme-selection" element={p(<ThemeSelection />)} />
      <Route path="/" element={p(<HomeFr />)} />
      <Route path="/quiz" element={p(<Quiz />)} />
      <Route path="/expert-chat" element={p(<ExpertChat />)} />
      <Route path="/magic-words" element={p(<MagicWords />)} />
      <Route path="/profile" element={p(<Profile />)} />
      <Route path="/profile/edit" element={p(<ProfileEdit />)} />
      <Route path="/terms" element={<LegalPendingFr />} />
      <Route path="/privacy" element={<LegalPendingFr />} />
      <Route path="/refund-policy" element={<LegalPendingFr />} />
      <Route path="/contact" element={<LegalPendingFr />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppContent() {
  const feedback = useFeedbackSurvey();
  // 25/09/2026: o convite de pesquisa abria POR CIMA do livro, no meio da
  // leitura (visto no render do leitor novo). Dentro de um leitor ele espera;
  // aparece quando ela sair do livro.
  const { pathname } = useLocation();
  const reading = pathname.startsWith('/read/') || pathname.startsWith('/ebook/');

  // 23/09/2026: o OneSignal saiu do app por decisão dele. O push estava morto desde 12/03
  // (SDK v16 chamado com a API v15) e o SDK era baixado em toda abertura, para todo mundo.
  // Quem ainda tem o service worker antigo instalado perde ele aqui, uma vez.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    void navigator.serviceWorker.getRegistrations().then((regs) => {
      regs
        .filter((r) => /OneSignal|\/push\/onesignal\//i.test(r.scope + ' ' + (r.active?.scriptURL ?? '')))
        .forEach((r) => void r.unregister());
    }).catch(() => undefined);
  }, []);

  return (
    <ErrorBoundary>
      <FeedbackInviteModal
        open={!IS_FR && feedback.shouldShowInvite && !reading}
        onAccept={feedback.accept}
        onDismiss={feedback.dismiss}
      />
      <FeedbackSurvey
        open={!IS_FR && feedback.shouldShowSurvey && !reading}
        onSubmit={feedback.submit}
        onClose={feedback.dismiss}
        isLoading={feedback.isLoading}
      />
    <Suspense fallback={<PageLoader />}>
      {IS_FR ? <FrRoutes /> : (
      <Routes>
        <Route path="/auth" element={<Auth />} />
        <Route path="/welcome" element={<Welcome />} />
        {/* A Cartpanda parou de vender em 06/04/2026: zero compras nos ultimos 30
            dias e UMA visita a esta tela em 90 dias, contra 640 da tela da
            Hotmart. A pagina propria tinha 894 linhas e 16 estados -- era a
            maior das tres, mantida para um gateway morto. O link antigo
            continua funcionando, com os parametros da compra intactos, e cai na
            tela generica de boas-vindas. Removida em 20/09/2026. */}
        <Route path="/welcome-cp" element={<Navigate to={`/welcome${window.location.search}`} replace />} />
        <Route path="/welcome-hp" element={<WelcomeHotmart />} />
        <Route path="/blocked" element={<Blocked />} />
        <Route path="/w" element={<Navigate to={`/welcome${window.location.search}`} replace />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/pwa-install" element={<ProtectedRoute><PWAInstall /></ProtectedRoute>} />
        <Route path="/pwa-check" element={<ProtectedRoute><PWACheck /></ProtectedRoute>} />
        <Route path="/theme-selection" element={<ProtectedRoute><ThemeSelection /></ProtectedRoute>} />

        {/* 28/09/2026 (decisão dele): sem chat nem botão de reembolso; o pedido vem pelo contato. */}
        <Route path="/refund" element={<Navigate to="/profile" replace />} />
        <Route
          path="/refund-status"
          element={
            <ProtectedRoute>
              <RefundStatus />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/scripts"
          element={
            <ProtectedRoute>
              <Scripts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scripts/:id"
          element={
            <ProtectedRoute>
              <PhraseDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/welcome-video"
          element={
            <ProtectedRoute>
              <OnboardingWelcome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quiz"
          element={
            <ProtectedRoute>
              <Quiz />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/edit"
          element={
            <ProtectedRoute>
              <ProfileEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/purchases"
          element={
            <ProtectedRoute>
              <Purchases />
            </ProtectedRoute>
          }
        />
        <Route
          path="/family"
          element={
            <ProtectedRoute>
              <Family />
            </ProtectedRoute>
          }
        />
        <Route path="/invite/:token" element={<Invite />} />
        <Route
          path="/family/welcome"
          element={
            <ProtectedRoute>
              <FamilyWelcome />
            </ProtectedRoute>
          }
        />
        {/* 22/09/2026: o Family Share não é mais vendido e o convite funciona para todos
            pela tela Family (58 donos convidaram em 30 dias). A tela antiga esperava um
            pagamento de "Family Access" que não existe mais; teve 0 visitas em 30 dias. */}
        <Route path="/family/setup" element={<Navigate to="/family" replace />} />
        {/* 24/09/2026: o Learn virou a aba Books, só de livros. As vídeo-aulas saíram
            do app, e a estante que morava em /library é a própria aba. Os endereços
            antigos (links salvos, notificações já entregues) caem nos livros. */}
        <Route path="/videos" element={<Navigate to="/learn?retired=lessons" replace />} />
        <Route path="/library" element={<Navigate to="/learn" replace />} />
        <Route path="/bonuses" element={<Navigate to="/learn" replace />} />
        <Route path="/learn/collection/:slug" element={<Navigate to="/learn?retired=lessons" replace />} />
        <Route
          path="/learn"
          element={
            <ProtectedRoute>
              <Learn />
            </ProtectedRoute>
          }
        />
        {/* 21/09/2026: as rotas antigas continuam úteis para quem tem link salvo. */}
        <Route path="/calm-mom-system" element={<Navigate to="/purchases#calm-mom-code" replace />} />
        <Route path="/premium-content" element={<Navigate to="/purchases" replace />} />
        <Route
          path="/purchases"
          element={
            <ProtectedRoute>
              <YourPurchases />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expert-chat"
          element={
            <ProtectedRoute>
              <ExpertChat />
            </ProtectedRoute>
          }
        />
        <Route
          path="/nutrition"
          element={
            <ProtectedRoute>
              <NutritionGuide />
            </ProtectedRoute>
          }
        />
        <Route
          path="/baby-phase"
          element={
            <ProtectedRoute>
              <BabyPhase />
            </ProtectedRoute>
          }
        />
        <Route
          path="/toddler-phase"
          element={
            <ProtectedRoute>
              <ToddlerPhase />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sleep-challenge"
          element={
            <ProtectedRoute>
              <SleepChallenge />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sleep-challenge/:type/:id"
          element={
            <ProtectedRoute>
              <SleepChallengeLesson />
            </ProtectedRoute>
          }
        />
        <Route
          path="/adhd-guide"
          element={
            <ProtectedRoute>
              <ADHDGuide />
            </ProtectedRoute>
          }
        />
        {/* 25/09: The Grandparent Code aposentado; o The Grandma Code o absorveu (decisão dele). */}
        <Route path="/read/the-grandparent-code" element={<Navigate to="/read/the-grandma-code" replace />} />
        <Route
          path="/read/:slug"
          element={
            <ProtectedRoute>
              <EbookReaderV3 />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guides"
          element={
            <ProtectedRoute>
              <Guides />
            </ProtectedRoute>
          }
        />
        {/* Redirect old programs route to guides */}
        <Route path="/programs" element={<Navigate to="/guides" replace />} />
        <Route
          path="/guides/:slug"
          element={
            <ProtectedRoute>
              <GuideDetail />
            </ProtectedRoute>
          }
        />
        {/* Redirect old programs/:slug route */}
        <Route path="/programs/:slug" element={<Navigate to="/guides" replace />} />
        <Route
          path="/guides/:slug/lesson/:number"
          element={
            <ProtectedRoute>
              <GuideLesson />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ebook/:ebookId"
          element={
            <ProtectedRoute>
              <EbookReader />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <Admin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/magic-words"
          element={
            <ProtectedRoute>
              <MagicWords />
            </ProtectedRoute>
          }
        />
        {/* 25/09/2026: a página de metodologia foi apagada (pedido dele; tinha
            percentuais sem fonte e "citations em toda frase"). Quem tiver o endereço
            salvo cai no livro que explica o método de verdade. */}
        <Route path="/methodology" element={<Navigate to="/read/the-3-magic-words" replace />} />
        <Route
          path="/generate-welcome-pdf"
          element={
            <ProtectedRoute>
              <GenerateWelcomePDF />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/:userId"
          element={
            <ProtectedRoute>
              <UserProfile />
            </ProtectedRoute>
          }
        />

        {/* Join Family - Public route for accepting invites (no auth required) */}
        <Route path="/join-family" element={<JoinFamily />} />

        {/* Gift - NOT in ProtectedRoute. Magic link sets session via hash fragment which needs time to process before ProtectedRoute checks auth */}
        <Route path="/gift" element={<Gift />} />

        {/* Legal Pages - Public */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/contact" element={<Contact />} />

        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      )}
    </Suspense>
    </ErrorBoundary>
  );
}

// reducedMotion="user" faz o Framer Motion obedecer o "Reduzir movimento" do
// sistema. O bloco @media do index.css só alcanca animacao CSS; tudo que o app
// anima por JS ignorava a preferencia ate aqui (17/09/2026).
const App = () => (
  <QueryClientProvider client={queryClient}>
    <MotionConfig reducedMotion="user">
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToTop />
        <RouteTracker />
        <AuthProvider>
          <ThemeProvider>
            <TooltipProvider>
              <NavigationProgress />
              <Toaster />
              <Sonner />
              <UpdateSnackbar />
              <ChildProfilesProvider>
                <TourProvider>
                  <AppContent />
                </TourProvider>
              </ChildProfilesProvider>
            </TooltipProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </MotionConfig>
  </QueryClientProvider>
);

export default App;
